import { applyCommand, invertCommand, propState, type Command, type EntityState, type PropState } from "../document/commands.ts";
import { sameJson } from "../document/same-json.ts";
import type { IssueIgnores, LayerDocument, WorldDocument } from "../document/types.ts";
import { canonicalMap } from "../document/map-commands.ts";
import { seamRemovalsFor } from "./layout-ops.ts";
import { layerOf, mapOf } from "./resolve-types.ts";
import { narrowedCells } from "./undo-cells.ts";
import { doorPlan, doorUpdatePlan } from "./undo-doors.ts";
import { absent, fieldsBack, type Plan } from "./undo-plan.ts";
import { tilesetPlanners } from "./undo-tilesets.ts";

/**
 * The inverse of an edit against the document as it is now (ADR-086 decision 27), key by key: a cell, a field
 * or an item goes back only where it still holds what the edit left; an added item, layer or map is removed
 * only if it is still as the edit left it; a removed one is restored with its content unless its id has been
 * taken; a moved layer goes back only if it is still where the edit put it. What someone else changed since is
 * left as they made it. The result is a list of commands that apply to the document in order, which then
 * travel as a new operation like any other edit.
 */

/**
 * An inverse: the commands that take an edit back as far as it can still be taken, in the order they apply
 * to the document it was built against, how many keys (cells, fields, items) they put back, how many were
 * left, and how many of those were left because their target is gone (a deleted layer, map or item) and not
 * because someone changed them: such a key can be taken back once its target returns.
 */
export interface Inverse {
  readonly commands: readonly Command[];
  readonly kept: number;
  readonly skipped: number;
  readonly gone: number;
}

/**
 * How to take back a change of a map's properties, key by key.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function propertiesPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "setMapProperties" }>): Plan {
  const map = mapOf(world, command.mapId);
  if (map === undefined) {
    return absent();
  }
  const back = fieldsBack(map.properties, command.before, command.after);
  return { tries: back.keys === 0 ? [] : [{ kind: "setMapProperties", mapId: map.id, before: map.properties, after: back.fields }], keys: back.keys, lost: back.lost };
}

/**
 * How to take back a change of a map's ignored issues, key by key: an ignore the edit set goes while it still
 * holds what the edit left, and one it removed comes back while the key is still free.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function ignoresPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "setIssueIgnores" }>): Plan {
  const map = mapOf(world, command.mapId);
  if (map === undefined) {
    return absent();
  }
  const held = map.ignoredIssues ?? {};
  const back = fieldsBack(held, command.before, command.after);
  return { tries: back.keys === 0 ? [] : [{ kind: "setIssueIgnores", mapId: map.id, before: held, after: back.fields as IssueIgnores }], keys: back.keys, lost: back.lost };
}

/**
 * How to take back a change of a prop, field by field: its frame, its position (x and y together), each flip,
 * its sort offset, its behaviour, whether it is kept as placed and its own footprint. A footprint's cells go back only onto the frame they
 * were of and onto a prop left without a behaviour (ADR-040 amendment of 2026-10-04); a frame or a behaviour
 * that goes back takes the footprint the prop holds now with it unless its own goes back too.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function propPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "updateProp" }>): Plan {
  const layer = layerOf(world, command.mapId, command.layerId);
  const prop = layer?.kind === "prop" ? layer.props.find((candidate) => candidate.id === command.propId) : undefined;
  const { before, after } = command;
  const changed = {
    frame: !sameJson(before.frame, after.frame),
    at: before.x !== after.x || before.y !== after.y,
    flipX: before.flipX !== after.flipX,
    flipY: before.flipY !== after.flipY,
    sortPixels: before.sortPixels !== after.sortPixels,
    behaviour: !sameJson(before.behaviour, after.behaviour),
    keepPlacement: (before.keepPlacement === true) !== (after.keepPlacement === true),
    footprint: !sameJson(before.footprint, after.footprint),
  };
  const total = Object.values(changed).filter(Boolean).length;
  if (prop === undefined) {
    return absent(Math.max(1, total));
  }
  const held = propState(prop);
  const back = {
    frame: changed.frame && sameJson(held.frame, after.frame),
    at: changed.at && held.x === after.x && held.y === after.y,
    flipX: changed.flipX && held.flipX === after.flipX,
    flipY: changed.flipY && held.flipY === after.flipY,
    sortPixels: changed.sortPixels && held.sortPixels === after.sortPixels,
    behaviour: changed.behaviour && sameJson(held.behaviour, after.behaviour),
    keepPlacement: changed.keepPlacement && (held.keepPlacement === true) === (after.keepPlacement === true),
    footprint: false,
  };
  const frameNext = back.frame ? before.frame : held.frame;
  const behaviourNext = back.behaviour ? before.behaviour : held.behaviour;
  back.footprint = changed.footprint && sameJson(held.footprint, after.footprint) && (before.footprint === undefined || (behaviourNext === undefined && sameJson(frameNext, before.frame)));
  const keys = Object.values(back).filter(Boolean).length;
  const behaviour = behaviourNext;
  const kept = sameJson(frameNext, held.frame) && behaviourNext === undefined ? held.footprint : undefined;
  const footprint = back.footprint ? before.footprint : kept;
  const keepPlacement = (back.keepPlacement ? before.keepPlacement : held.keepPlacement) === true;
  const next: PropState = {
    frame: back.frame ? before.frame : held.frame,
    x: back.at ? before.x : held.x,
    y: back.at ? before.y : held.y,
    flipX: back.flipX ? before.flipX : held.flipX,
    flipY: back.flipY ? before.flipY : held.flipY,
    sortPixels: back.sortPixels ? before.sortPixels : held.sortPixels,
    ...(behaviour === undefined ? {} : { behaviour }),
    ...(keepPlacement ? { keepPlacement: true as const } : {}),
    ...(footprint === undefined ? {} : { footprint }),
  };
  return { tries: keys === 0 ? [] : [{ kind: "updateProp", mapId: command.mapId, layerId: command.layerId, propId: command.propId, before: held, after: next }], keys, lost: total - keys };
}

/**
 * How to take back a change of an entity: its position as one key, and each key of its fields.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function entityPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "updateEntity" }>): Plan {
  const layer = layerOf(world, command.mapId, command.layerId);
  const entity = layer?.kind === "entity" ? layer.entities.find((candidate) => candidate.id === command.entityId) : undefined;
  const { before, after } = command;
  const moved = before.x !== after.x || before.y !== after.y;
  if (entity === undefined) {
    return absent();
  }
  const held: EntityState = { x: entity.x, y: entity.y, fields: entity.fields };
  const fields = fieldsBack(entity.fields, before.fields, after.fields);
  const back = moved && entity.x === after.x && entity.y === after.y;
  const keys = fields.keys + (back ? 1 : 0);
  const next: EntityState = { x: back ? before.x : entity.x, y: back ? before.y : entity.y, fields: fields.fields };
  return { tries: keys === 0 ? [] : [{ kind: "updateEntity", mapId: command.mapId, layerId: command.layerId, entityId: command.entityId, before: held, after: next }], keys, lost: fields.lost + (moved && !back ? 1 : 0) };
}

/**
 * How to take back an added or a removed prop or entity: an added one is removed only if it is still as it
 * was added; a removed one is put back at its place, or last when the layer is shorter now, unless its id has
 * been taken.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function itemPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "addProp" | "removeProp" | "addEntity" | "removeEntity" }>): Plan {
  const layer = layerOf(world, command.mapId, command.layerId);
  const at = { mapId: command.mapId, layerId: command.layerId };
  const none: Plan = { tries: [], keys: 0, lost: 1 };
  if (command.kind === "addProp" || command.kind === "removeProp") {
    if (layer?.kind !== "prop") {
      return absent();
    }
    const index = layer.props.findIndex((candidate) => candidate.id === command.prop.id);
    if (command.kind === "addProp") {
      const held = layer.props[index];
      if (held === undefined) {
        return absent();
      }
      return sameJson(held, command.prop) ? { tries: [{ kind: "removeProp", ...at, index, prop: held }], keys: 1, lost: 0 } : none;
    }
    return index < 0 ? { tries: [{ kind: "addProp", ...at, index: Math.min(command.index, layer.props.length), prop: command.prop }], keys: 1, lost: 0 } : none;
  }
  if (layer?.kind !== "entity") {
    return absent();
  }
  const index = layer.entities.findIndex((candidate) => candidate.id === command.entity.id);
  if (command.kind === "addEntity") {
    const held = layer.entities[index];
    if (held === undefined) {
      return absent();
    }
    return sameJson(held, command.entity) ? { tries: [{ kind: "removeEntity", ...at, index, entity: held }], keys: 1, lost: 0 } : none;
  }
  return index < 0 ? { tries: [{ kind: "addEntity", ...at, index: Math.min(command.index, layer.entities.length), entity: command.entity }], keys: 1, lost: 0 } : none;
}

/**
 * How to take back an added, a removed or a moved layer. An added layer is removed only if it is still as it
 * was added, with the group made for it when that leaves the group empty. A removed layer is restored with its
 * content at its place, or on top when the map is shorter now, unless its id has been taken; in the group it
 * was in when the map still has it or the removal took it too, and in no group otherwise. A moved layer goes
 * back only if it is still where the edit put it, with the Players band it had when that still fits.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function layerPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "addLayer" | "removeLayer" | "moveLayer" }>): Plan {
  const map = mapOf(world, command.mapId);
  const none: Plan = { tries: [], keys: 0, lost: 1 };
  if (map === undefined) {
    return absent();
  }
  if (command.kind === "moveLayer") {
    if (command.layerId !== undefined && !map.layers.some((candidate) => candidate.id === command.layerId)) {
      return absent();
    }
    if (command.layerId !== undefined && map.layers[command.to]?.id !== command.layerId) {
      return none;
    }
    const plain: Command = { kind: "moveLayer", mapId: map.id, from: command.to, to: Math.min(command.from, map.layers.length - 1), ...(command.layerId === undefined ? {} : { layerId: command.layerId }) };
    return { tries: [invertCommand(command), plain], keys: 1, lost: 0 };
  }
  const index = map.layers.findIndex((candidate) => candidate.id === command.layer.id);
  if (command.kind === "addLayer") {
    const held = map.layers[index];
    if (held === undefined) {
      return absent();
    }
    if (!sameJson(held, command.layer)) {
      return none;
    }
    const plain: Command = { kind: "removeLayer", mapId: map.id, index, layer: held };
    const made = (map.groups ?? []).find((group) => group.id === command.group?.id);
    return { tries: made === undefined ? [plain] : [{ ...plain, group: made }, plain], keys: 1, lost: 0 };
  }
  if (index >= 0) {
    return none;
  }
  const place = { kind: "addLayer" as const, mapId: map.id, index: Math.min(command.index, map.layers.length) };
  const { group: _group, ...loose } = command.layer;
  const tries: Command[] = [...(command.group === undefined ? [] : [{ ...place, layer: command.layer, group: command.group }]), { ...place, layer: command.layer }, { ...place, layer: loose as LayerDocument }];
  return { tries, keys: 1, lost: 0 };
}

/**
 * How to take back an added or a removed map: an added map is removed only if it is still as it was added, with
 * the seams that name it now, as a removal resolved on the server takes them (ADR-109 decision 6); a removed one is restored with its content at its place, or last, unless its id has been taken.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The plan.
 */
function mapPlan(world: WorldDocument, command: Extract<Command, { readonly kind: "addMap" | "removeMap" }>): Plan {
  const index = world.maps.findIndex((candidate) => candidate.id === command.map.id);
  const held = world.maps[index];
  if (command.kind === "addMap") {
    if (held === undefined) {
      return absent();
    }
    if (!sameJson(held, canonicalMap(command.map))) {
      return { tries: [], keys: 0, lost: 1 };
    }
    const seams = seamRemovalsFor(world, held.id);
    const removal: Command = { kind: "removeMap", index, map: held };
    return { tries: [seams.length === 0 ? removal : { kind: "compound", commands: [...seams, removal] }], keys: 1, lost: 0 };
  }
  return held === undefined ? { tries: [{ kind: "addMap", index: Math.min(command.index, world.maps.length), map: command.map }], keys: 1, lost: 0 } : { tries: [], keys: 0, lost: 1 };
}

/**
 * How each part of a command may be taken back, each part planned against the document the parts before it
 * left.
 *
 * @param command The command as it applied; never a compound.
 * @returns One planner a part. A command of one key is taken back by its exact inverse, which the document
 *   accepts only while the key still holds what the command left.
 */
function planners(command: Exclude<Command, { readonly kind: "compound" }>): readonly ((world: WorldDocument) => Plan)[] {
  switch (command.kind) {
    case "paintCorners":
    case "paintCollision":
    case "paintZone":
    case "paintDoor":
    case "setTiles":
    case "fillCells":
      return [
        (world) => {
          const narrowed = narrowedCells(world, command);
          return { tries: narrowed.command === null ? [] : [narrowed.command], keys: narrowed.kept, lost: narrowed.skipped, gone: narrowed.gone };
        },
      ];
    case "setMapProperties":
      return [(world) => propertiesPlan(world, command)];
    case "setIssueIgnores":
      return [(world) => ignoresPlan(world, command)];
    case "updateProp":
      return [(world) => propPlan(world, command)];
    case "updateEntity":
      return [(world) => entityPlan(world, command)];
    case "addProp":
    case "removeProp":
    case "addEntity":
    case "removeEntity":
      return [(world) => itemPlan(world, command)];
    case "addLayer":
    case "removeLayer":
    case "moveLayer":
      return [(world) => layerPlan(world, command)];
    case "addDoor":
    case "removeDoor":
      return [(world) => doorPlan(world, command)];
    case "updateDoor":
      return [(world) => doorUpdatePlan(world, command)];
    case "addMap":
    case "removeMap":
      return [(world) => mapPlan(world, command)];
    case "changeTilesets":
      return tilesetPlanners(command);
    default:
      return [(world) => (targetGone(world, command) ? absent() : { tries: [invertCommand(command)], keys: 1, lost: 0 })];
  }
}

/**
 * Whether what a command of one key writes to is gone: its map, its layer or its group.
 *
 * @param world The document as it is now.
 * @param command The command.
 * @returns True when the map it names is gone, or the layer or the group it names in that map.
 */
function targetGone(world: WorldDocument, command: Exclude<Command, { readonly kind: "compound" }>): boolean {
  if (!("mapId" in command)) {
    return false;
  }
  const map = mapOf(world, command.mapId);
  if (map === undefined) {
    return true;
  }
  if ("layerId" in command && command.layerId !== undefined && !map.layers.some((layer) => layer.id === command.layerId)) {
    return true;
  }
  return command.kind === "renameGroup" && !(map.groups ?? []).some((group) => group.id === command.groupId);
}

/**
 * A list of commands with every compound replaced by its parts, in order.
 *
 * @param commands The commands.
 * @returns The commands, none of them a compound.
 */
function flattened(commands: readonly Command[]): Exclude<Command, { readonly kind: "compound" }>[] {
  return commands.flatMap((command) => (command.kind === "compound" ? flattened(command.commands) : [command]));
}

/**
 * Builds the inverse of an edit against a document.
 *
 * @param document The document as it is now; never written into.
 * @param commands The edit's commands, in the order they applied.
 * @param travels Whether a command can be sent as an operation from a document; every command by default.
 * @returns The commands that take the edit back as far as it still can be, last change first. Each applies to
 *   the document the ones before it leave, starting from `document`, and never throws there. A part of the
 *   edit is left, and counted as skipped, where the document no longer holds what the edit left, where the
 *   document refuses the command for any other reason, where the command cannot travel, and where its target
 *   is gone, which `gone` counts apart.
 */
export function inverseAgainst(document: WorldDocument, commands: readonly Command[], travels: (command: Command, document: WorldDocument) => boolean = () => true): Inverse {
  const inverse: Command[] = [];
  let working = document;
  let kept = 0;
  let skipped = 0;
  let gone = 0;
  for (const command of flattened(commands).reverse()) {
    for (const planner of planners(command)) {
      let plan: Plan;
      try {
        plan = planner(working);
      } catch {
        plan = { tries: [], keys: 0, lost: 1 };
      }
      skipped += plan.lost + (plan.gone ?? 0);
      gone += plan.gone ?? 0;
      let applied = false;
      for (const candidate of plan.tries) {
        try {
          const next = applyCommand(working, candidate);
          if (travels(candidate, working)) {
            inverse.push(...(candidate.kind === "compound" ? candidate.commands : [candidate]));
            working = next;
            applied = true;
            break;
          }
        } catch {
          applied = false;
        }
      }
      kept += applied ? plan.keys : 0;
      skipped += applied ? 0 : plan.keys;
    }
  }
  return { commands: inverse, kept, skipped, gone };
}
